// @ts-nocheck
import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useRbac, isAdmin } from "@shared/rbac/guards";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronDown } from "lucide-react";
import { Badge, Tooltip, cn } from "@ui/core";

export type NavItem = {
  label: string;
  to?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  rolesAllowed?: string[];
  perm?: string;
  permissionsRequired?: string[];
  children?: NavItem[];
  target?: string;
  badge?: string;
  badgeVariant?:
    | "default"
    | "secondary"
    | "destructive"
    | "outline"
    | "success"
    | "warning";
};

function allowedByRbac(
  item: NavItem,
  userRoles: string[],
  userPerms?: string[],
) {
  // permissions check
  if (item.perm && !userPerms?.includes(item.perm)) return false;
  if (item.permissionsRequired?.some((permission) => !userPerms?.includes(permission))) return false;

  // roles check
  const rolesAllowed = item.rolesAllowed;
  if (!rolesAllowed || rolesAllowed.length === 0) return true;
  return rolesAllowed.some((r) => userRoles.includes(r));
}

function canShow(
  item: NavItem,
  user: { roles: string[]; permissions?: string[] } | null,
) {
  // Admins can see everything
  if (isAdmin(user)) return true;

  const roles = user?.roles ?? [];
  const perms = user?.permissions;

  if (item.children && item.children.length) {
    return item.children.some((c) => canShow(c, user));
  }

  return allowedByRbac(item, roles, perms);
}

function normalizePathname(pathname?: string) {
  const path = (pathname || "/").split(/[?#]/)[0] || "/";
  if (path.length <= 1) return "/";
  return path.replace(/\/+$/, "");
}

export function pathMatches(pathname: string, to?: string) {
  if (!to || !to.startsWith("/")) return false;
  const currentPath = normalizePathname(pathname);
  const targetPath = normalizePathname(to);
  return (
    currentPath === targetPath ||
    (targetPath !== "/" && currentPath.startsWith(targetPath + "/"))
  );
}

const EXPANDED_STORAGE_KEY = "storizone:sidebar:expanded";
const LEGACY_SCROLL_STORAGE_KEY = "storizone:sidebar:scroll";
const SCROLL_STORAGE_KEYS = {
  desktop: "storizone:sidebar:scroll:desktop",
  mobile: "storizone:sidebar:scroll:mobile",
} as const;
const ACTIVE_SCROLL_PADDING = 12;
const DESKTOP_SIDEBAR_MEDIA_QUERY = "(min-width: 1024px)";
type SidebarViewport = keyof typeof SCROLL_STORAGE_KEYS;
type ScrollTarget = { scroller: HTMLElement | null; viewport: SidebarViewport };

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

function getInitialIsLgUp() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY).matches;
}

function renderBadge(item: NavItem) {
  if (!item.badge) return null;
  return (
    <Badge
      variant={item.badgeVariant || "warning"}
      className="ml-2 shrink-0 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-normal"
    >
      {item.badge}
    </Badge>
  );
}

export function Sidebar({
  items,
  isOpen,
  isCollapsed = false,
  onExpand,
  onClose,
}: {
  items: NavItem[];
  isOpen: boolean;
  isCollapsed?: boolean;
  onExpand?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("nav");
  const { user } = useRbac();

  const { location } = useRouterState({
    select: (s) => ({ location: s.location }),
  });
  const desktopScrollRef = React.useRef<HTMLElement | null>(null);
  const mobileScrollRef = React.useRef<HTMLElement | null>(null);

  // Only auto-close on navigation for mobile viewports
  const [isLgUp, setIsLgUp] = React.useState(getInitialIsLgUp);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(DESKTOP_SIDEBAR_MEDIA_QUERY);
    const update = () => setIsLgUp(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Expanded groups state persisted in localStorage
  const [expanded, setExpanded] = React.useState<Set<string>>(() => {
    try {
      const raw =
        typeof window !== "undefined"
          ? window.localStorage.getItem(EXPANDED_STORAGE_KEY)
          : null;
      const arr: string[] = raw ? JSON.parse(raw) : [];
      return new Set(
        arr.filter((value) => typeof value === "string" && value.length > 0),
      );
    } catch {
      return new Set();
    }
  });
  const persistExpanded = React.useCallback((next: Set<string>) => {
    setExpanded(new Set(next));
    try {
      if (typeof window !== "undefined")
        window.localStorage.setItem(
          EXPANDED_STORAGE_KEY,
          JSON.stringify(Array.from(next)),
        );
    } catch {}
  }, []);
  const getActiveScrollTarget = React.useCallback((): ScrollTarget => {
    if (isLgUp && desktopScrollRef.current)
      return { scroller: desktopScrollRef.current, viewport: "desktop" };
    if (!isLgUp && mobileScrollRef.current)
      return { scroller: mobileScrollRef.current, viewport: "mobile" };
    if (desktopScrollRef.current)
      return { scroller: desktopScrollRef.current, viewport: "desktop" };
    if (mobileScrollRef.current)
      return { scroller: mobileScrollRef.current, viewport: "mobile" };

    return { scroller: null, viewport: isLgUp ? "desktop" : "mobile" };
  }, [isLgUp]);
  const persistScrollTop = React.useCallback(
    (viewport: SidebarViewport, scrollTop: number) => {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(
            SCROLL_STORAGE_KEYS[viewport],
            String(Math.max(0, Math.round(scrollTop))),
          );
        }
      } catch {}
    },
    [],
  );
  const restoreScrollTop = React.useCallback(
    ({ scroller, viewport }: ScrollTarget) => {
      if (!scroller) return;
      try {
        const raw =
          typeof window !== "undefined"
            ? (window.localStorage.getItem(SCROLL_STORAGE_KEYS[viewport]) ??
              window.localStorage.getItem(LEGACY_SCROLL_STORAGE_KEY))
            : null;
        const scrollTop = raw === null ? 0 : Number(raw);
        if (Number.isFinite(scrollTop))
          scroller.scrollTop = Math.max(0, scrollTop);
      } catch {}
    },
    [],
  );
  const ensureActiveItemVisible = React.useCallback(() => {
    const { scroller, viewport } = getActiveScrollTarget();
    if (!scroller) return;

    const active = scroller.querySelector(
      '[data-nav-active="true"]',
    ) as HTMLElement | null;
    if (!active) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const scrollerHeight = scrollerRect.height || scroller.clientHeight;
    const activeHeight = activeRect.height || active.offsetHeight;

    if (scrollerHeight <= 0 || activeHeight <= 0) return;

    if (activeRect.top < scrollerRect.top + ACTIVE_SCROLL_PADDING) {
      scroller.scrollTop +=
        activeRect.top - scrollerRect.top - ACTIVE_SCROLL_PADDING;
      persistScrollTop(viewport, scroller.scrollTop);
    } else if (
      activeRect.bottom >
      scrollerRect.bottom - ACTIVE_SCROLL_PADDING
    ) {
      scroller.scrollTop +=
        activeRect.bottom - scrollerRect.bottom + ACTIVE_SCROLL_PADDING;
      persistScrollTop(viewport, scroller.scrollTop);
    }
  }, [getActiveScrollTarget, persistScrollTop]);
  useIsomorphicLayoutEffect(() => {
    restoreScrollTop(getActiveScrollTarget());
  }, [getActiveScrollTarget, isOpen, isCollapsed, restoreScrollTop]);
  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.requestAnimationFrame !== "function") {
      ensureActiveItemVisible();
      return;
    }
    ensureActiveItemVisible();
    const frame = window.requestAnimationFrame(() => ensureActiveItemVisible());
    return () => window.cancelAnimationFrame(frame);
  }, [
    ensureActiveItemVisible,
    expanded,
    isCollapsed,
    isOpen,
    items,
    location.pathname,
  ]);

  // Accessible keyboard focus management
  const focusablesRef = React.useRef<HTMLElement[]>([]);
  const setFocusable = (el: HTMLElement | null) => {
    if (!el) return;
    // avoid duplicates
    if (!focusablesRef.current.includes(el)) focusablesRef.current.push(el);
  };
  const onKeyDownList = (e: React.KeyboardEvent) => {
    const els = focusablesRef.current.filter(Boolean);
    const idx = els.findIndex((el) => el === document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = els[Math.min(els.length - 1, idx + 1)] || els[idx];
      next?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = els[Math.max(0, idx - 1)] || els[idx];
      prev?.focus();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const target = document.activeElement as HTMLElement | null;
      const id = target?.dataset?.groupId;
      if (!id) return;
      const isExpanded = expanded.has(id);
      if (e.key === "ArrowLeft" && isExpanded) {
        e.preventDefault();
        const next = new Set(expanded);
        next.delete(id);
        persistExpanded(next);
      } else if (e.key === "ArrowRight" && !isExpanded) {
        e.preventDefault();
        const next = new Set(expanded);
        next.add(id);
        persistExpanded(next);
      }
    }
  };

  function renderItem(it: NavItem): React.ReactNode {
    const visible = canShow(it, user ?? null);
    if (!visible) return null;

    const label = t(it.label);
    const isSpecialAction = it.label === "Open Store";
    const visibleChildren =
      it.children?.filter((c) => canShow(c, user ?? null)) ?? [];
    const activeChild = visibleChildren
      .filter((c) => pathMatches(location.pathname, c.to))
      .sort((a, b) => (b.to?.length || 0) - (a.to?.length || 0))[0];
    const hasActiveChild = Boolean(activeChild);

    if (it.children && it.children.length && !isCollapsed) {
      const id = it.label;
      const isGroupOpen = expanded.has(id) || hasActiveChild;
      const toggle = () => {
        const next = new Set(expanded);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        persistExpanded(next);
      };
      return (
        <li key={id}>
          <button
            type="button"
            data-nav-focusable="true"
            data-group-id={id}
            aria-expanded={isGroupOpen}
            onClick={toggle}
            ref={setFocusable as any}
            className={cn(
              "group flex min-h-10 w-full items-center justify-between rounded-md border border-transparent px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              hasActiveChild
                ? "border-primary/20 bg-primary/10 text-primary shadow-sm"
                : "text-foreground/80 hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <span className="flex min-w-0 items-center gap-3">
              {it.icon && <span className="h-4 w-4 shrink-0">{it.icon}</span>}
              <span className="flex min-w-0 items-center">
                <span className="truncate">{label}</span>
                {renderBadge(it)}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="ml-auto text-muted-foreground/50 transition-colors group-hover:text-foreground"
            >
              {isGroupOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          </button>

          {isGroupOpen ? (
            <ul className="ml-5 mt-1 space-y-1 border-l border-border/70 pl-3">
              {visibleChildren.map((c) => {
                const active = activeChild?.to === c.to;
                return (
                  <li key={(c.to || c.label) + ":child"}>
                    {c.to ? (
                      <Link
                        to={c.to}
                        preload="intent"
                        preloadDelay={50}
                        data-nav-focusable="true"
                        data-nav-active={active ? "true" : undefined}
                        aria-current={active ? "page" : undefined}
                        ref={setFocusable as any}
                        className={cn(
                          "flex min-h-10 min-w-0 items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                          active
                            ? "bg-primary/10 font-medium text-primary"
                            : "text-muted-foreground",
                        )}
                        onClick={() => {
                          if (!isLgUp) onClose();
                        }}
                      >
                        {c.icon}
                        <span className="flex min-w-0 items-center">
                          <span className="truncate">{t(c.label)}</span>
                          {renderBadge(c)}
                        </span>
                      </Link>
                    ) : (
                      <span className="px-2 py-1.5 text-muted-foreground">
                        <span className="flex min-w-0 items-center">
                          <span>{t(c.label)}</span>
                          {renderBadge(c)}
                        </span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </li>
      );
    }

    // leaf item or collapsed group item
    const active = it.to
      ? pathMatches(location.pathname, it.to)
      : hasActiveChild;

    const commonClasses = cn(
      "flex items-center rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
      isCollapsed
        ? "justify-center p-2 mx-auto w-10 h-10"
        : "min-h-10 gap-3 px-3 py-2 w-full",
      active
        ? "bg-primary/10 text-primary"
        : "text-muted-foreground hover:bg-primary/5 hover:text-primary",
      isSpecialAction &&
        isCollapsed &&
        "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
    );

    const iconElement = it.icon ? (
      <span
        className={cn(
          "shrink-0 transition-transform",
          isCollapsed
            ? isSpecialAction
              ? "h-5 w-5 scale-110"
              : "h-5 w-5"
            : "h-4 w-4",
        )}
      >
        {it.icon}
      </span>
    ) : null;

    let element: React.ReactNode;

    if (it.to && it.target === "_blank") {
      element = (
        <a
          href={it.to}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={isCollapsed ? label : undefined}
          data-nav-focusable="true"
          data-nav-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          ref={setFocusable as any}
          onClick={(e) => {
            e.stopPropagation();
            it.onClick?.();
            if (!isLgUp) onClose();
          }}
          className={commonClasses}
        >
          {iconElement}
          {!isCollapsed && (
            <span className="flex min-w-0 items-center">
              <span>{label}</span>
              {renderBadge(it)}
            </span>
          )}
        </a>
      );
    } else if (it.to) {
      element = (
        <Link
          to={it.to}
          preload="intent"
          preloadDelay={50}
          aria-label={isCollapsed ? label : undefined}
          data-nav-focusable="true"
          data-nav-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          ref={setFocusable as any}
          className={commonClasses}
          onClick={() => {
            if (!isLgUp) onClose();
          }}
        >
          {iconElement}
          {!isCollapsed && (
            <span className="flex min-w-0 items-center">
              <span>{label}</span>
              {renderBadge(it)}
            </span>
          )}
        </Link>
      );
    } else if (it.onClick) {
      element = (
        <button
          type="button"
          aria-label={isCollapsed ? label : undefined}
          data-nav-focusable="true"
          data-nav-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          ref={setFocusable as any}
          className={commonClasses}
          onClick={() => {
            it.onClick?.();
            if (!isLgUp) onClose();
          }}
        >
          {iconElement}
          {!isCollapsed && (
            <span className="flex min-w-0 items-center">
              <span>{label}</span>
              {renderBadge(it)}
            </span>
          )}
        </button>
      );
    } else if (isCollapsed && it.children && it.children.length) {
      element = (
        <button
          type="button"
          data-nav-active={active ? "true" : undefined}
          aria-current={active ? "page" : undefined}
          aria-label={label}
          aria-expanded={false}
          data-nav-focusable="true"
          data-group-id={it.label}
          ref={setFocusable as any}
          onClick={() => {
            const next = new Set(expanded);
            next.add(it.label);
            persistExpanded(next);
            onExpand?.();
          }}
          className={cn(commonClasses, !active && "opacity-70")}
        >
          {iconElement}
        </button>
      );
    } else {
      element = (
        <div className={cn(commonClasses, "cursor-not-allowed opacity-40")}>
          {iconElement}
          {!isCollapsed && (
            <span className="flex min-w-0 items-center">
              <span>{label}</span>
              {renderBadge(it)}
            </span>
          )}
        </div>
      );
    }

    if (isCollapsed) {
      return (
        <li key={it.to || it.label} className="flex justify-center">
          <Tooltip label={label} side="right" className="font-semibold">
            {element as React.ReactElement}
          </Tooltip>
        </li>
      );
    }

    return <li key={it.to || it.label}>{element}</li>;
  }

  // reset focusable registry each render
  focusablesRef.current = [];

  const renderContent = (
    scrollRef: React.MutableRefObject<HTMLElement | null>,
    viewport: SidebarViewport,
  ) => (
    <nav
      ref={scrollRef}
      className={cn(
        "h-full overflow-y-auto overscroll-contain bg-transparent py-4 transition-all duration-300",
        viewport === "desktop" ? "max-h-[calc(100vh-5rem)]" : "max-h-screen",
        isCollapsed ? "px-1" : "px-2",
      )}
      aria-label={t("Navigation")}
      onScroll={(event) =>
        persistScrollTop(viewport, event.currentTarget.scrollTop)
      }
    >
      <ul
        className={cn(
          "space-y-2 text-sm",
          isCollapsed && "flex flex-col items-center",
        )}
        onKeyDown={onKeyDownList}
      >
        {items.map((it) => renderItem(it))}
      </ul>
    </nav>
  );

  return (
    <>
      {/* Desktop */}
      <div
        id="admin-sidebar"
        className={cn(
          "hidden lg:block transition-all duration-300",
          isCollapsed ? "w-full" : "w-full",
        )}
        data-testid="sidebar"
      >
        <div className="sticky top-16 overflow-hidden rounded-xl border border-white/20 bg-background/60 backdrop-blur-xl shadow-sm transition-all duration-300">
          {renderContent(desktopScrollRef, "desktop")}
        </div>
      </div>

      {/* Mobile */}
      {isOpen ? (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        >
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={onClose}
          />
          <div className="absolute left-0 top-0 h-full w-72 bg-background/95 backdrop-blur-xl shadow-2xl transition-transform duration-300">
            {renderContent(mobileScrollRef, "mobile")}
          </div>
        </div>
      ) : null}
    </>
  );
}

export default Sidebar;
